import React, { useState } from "react";
import { FOLDERS } from "../../../doseeData/utils/Utils";
import { defaultImage } from "../../../../middlewares/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../../middlewares/libraries/imageKit/ImageKitLibrary";
import { useRegionalBlocksContext } from "../../../../context/regionalBlocks/RegionalBlocksContext";
import {
  Title,
  GoBack,
  TextArea,
  MyMessage,
  InputField,
  MyActionButton,
} from "../../component/Component";
import {
  FiGlobe,
  FiMapPin,
  FiCalendar,
  FiLink,
  FiFlag,
  FiTarget,
  FiEye,
} from "react-icons/fi";

const Add = () => {
  const { createOneRegionalBlocks } = useRegionalBlocksContext();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [name, setName] = useState(null);
  const [acronym, setAcronym] = useState(null);
  const [headquarters, setHeadquarters] = useState(null);
  const [objectives, setObjectives] = useState(null);
  const [debuted_on, setDebutedOn] = useState(null);
  const [mission, setMission] = useState(null);
  const [vision, setVision] = useState(null);
  const [description, setDescription] = useState(null);

  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");
  const [website, setWebsite] = useState(null);

  const regionalBlocksRoutes = "/regional/blocks";

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setName(null);
    setAcronym(null);
    setHeadquarters(null);
    setObjectives(null);
    setDebutedOn(null);
    setMission(null);
    setVision(null);
    setDescription(null);
    setThumbnail(defaultImage);
    setThumbnailId("image_cdn_id");
    setWebsite(null);
  };

  const responses = (isError, msg) => {
    setError(isError);
    setSuccess(!isError);
    setLoading(false);
    setErrorMessage(msg);
  };

  const addOneRegionalBlocksHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      return responses(true, "Image Required");
    }
    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      name,
      acronym,
      headquarters,
      chief_officer: "...",
      objectives,
      debuted_on,
      mission,
      vision,
      description,
      thumbnail,
      image_cdn_id: thumbnailId,
      website,
    };

    const { status } = await createOneRegionalBlocks(data);
    if (status === 200) {
      cleanFields();
      responses(false, message);
    } else {
      responses(true, message);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-7xl p-4 pb-28">
        <GoBack props={{ route: regionalBlocksRoutes }} />

        <form onSubmit={addOneRegionalBlocksHandler} className="mt-4">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-gray-900 text-white">
                  <FiGlobe className="h-5 w-5" />
                </span>
                <div>
                  <h1 className="text-base font-semibold tracking-tight text-gray-900">
                    Create Regional Block
                  </h1>
                  <p className="mt-0.5 text-xs text-gray-600">
                    Provide identity, purpose, and metadata.
                  </p>
                </div>
              </div>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold text-gray-700">
                New entry
              </span>
            </div>

            <div className="border-t border-gray-100" />

            <div className="px-5 py-5">
              <div className="mb-4">
                <Title props={{ title: "Regional Block Details" }} />
              </div>

              <div className="mb-4">
                <MyMessage
                  error={error}
                  message={message}
                  success={success}
                  onExpired={() => {
                    setError(false);
                    setSuccess(false);
                  }}
                  onClick={() => {
                    setError(false);
                    setSuccess(false);
                    setErrorMessage("");
                  }}
                />
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-gray-700">
                  <FiEye className="h-4 w-4" />
                  <span>Cover Image</span>
                </div>
                <ImageKitUpload
                  thumbnail={thumbnail}
                  thumbnailId={thumbnailId}
                  setThumbnail={setThumbnail}
                  setThumbnailId={setThumbnailId}
                  folder={FOLDERS.REGIONAL_BLOCKS}
                />
                <p className="mt-2 text-[11px] text-gray-500">
                  Recommended: 1200×675px, JPG/PNG.
                </p>
              </div>

              <div className="mt-6 grid gap-6 lg:grid-cols-5">
                <div className="lg:col-span-3">
                  <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-gray-700">
                      <FiFlag className="h-4 w-4" />
                      <span>Identity</span>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <InputField
                        props={{
                          id: "title",
                          title: "Name",
                          type: "text",
                          required: true,
                          value: name || "",
                          onChange: (e) => {
                            setName(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                      <InputField
                        props={{
                          id: "acronym",
                          title: "Acronym",
                          type: "text",
                          value: acronym || "",
                          onChange: (e) => {
                            setAcronym(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                      <InputField
                        props={{
                          id: "website",
                          title: "Website",
                          type: "text",
                          value: website || "",
                          onChange: (e) => {
                            setWebsite(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                      <InputField
                        props={{
                          id: "headquarters",
                          title: "Headquarters",
                          type: "text",
                          value: headquarters || "",
                          onChange: (e) => {
                            setHeadquarters(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                      <InputField
                        props={{
                          id: "debuted_on",
                          title: "Debuted On",
                          type: "text",
                          value: debuted_on || "",
                          onChange: (e) => {
                            setDebutedOn(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-600 ring-1 ring-gray-200">
                        <FiMapPin className="h-3.5 w-3.5" />
                        <span>HQ location</span>
                      </div>
                      <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-600 ring-1 ring-gray-200">
                        <FiCalendar className="h-3.5 w-3.5" />
                        <span>Founding year/date</span>
                      </div>
                      <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-600 ring-1 ring-gray-200">
                        <FiLink className="h-3.5 w-3.5" />
                        <span>Official website</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-gray-700">
                      <FiTarget className="h-4 w-4" />
                      <span>Purpose & Narrative</span>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <TextArea
                        props={{
                          id: "objectives",
                          title: "Objectives",
                          type: "text",
                          value: objectives || "",
                          onChange: (e) => {
                            setObjectives(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                      <TextArea
                        props={{
                          id: "mission",
                          title: "Mission",
                          type: "text",
                          value: mission || "",
                          onChange: (e) => {
                            setMission(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                      <TextArea
                        props={{
                          id: "vision",
                          title: "Vision",
                          type: "text",
                          value: vision || "",
                          onChange: (e) => {
                            setVision(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                      <TextArea
                        props={{
                          id: "description",
                          title: "Description",
                          type: "text",
                          required: true,
                          value: description || "",
                          onChange: (e) => {
                            setDescription(e.target.value);
                            setError(null);
                            setSuccess(null);
                          },
                        }}
                      />
                    </div>

                    <p className="mt-2 text-[11px] text-gray-500">
                      Keep the summary concise and informative.
                    </p>
                  </div>
                </div>

                {/* Right column placeholder to balance layout on large screens */}
                <div className="lg:col-span-2 hidden lg:block" />
              </div>
            </div>
          </div>

          {/* Sticky bottom action bar */}
          <div className="sticky bottom-0 left-0 right-0 z-20 -mx-4 border-t border-gray-200 bg-white/90 backdrop-blur">
            <div className="mx-auto max-w-7xl px-4 py-0 ">
              <div className="flex items-center justify-end">
                <MyActionButton
                  loading={loading}
                  title="Add Regional Block"
                  onClick={() => {
                    setError(false);
                    setSuccess(false);
                    setErrorMessage(null);
                  }}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Add;
